import { CHART_TYPES } from '../../../../components/Chart/constants/chartTypes';
import {
  ChartSortEnum,
  IGeoMapConfig,
  ChartNumberType,
  YAxisPosition,
  ICentralYAxisSettings,
} from '../../../../components/Chart/interfaces';
import { QueryType, TimeUnit } from '../../../../utils/apollo/graphqlSchema';
import { findByKey } from '../../../../utils/redux/util';
import {
  IPathNodeCustomization,
  NodeCustomizationScope,
} from '../../../../components/Chart/SankeyChart/interfaces';
import { assertCondition, assertNotNil } from '../../../../utils/types';
import { Draft, PayloadAction } from '@reduxjs/toolkit';
import { MaybeNil } from '../../../../utils/interfaces';
import { IAnalysesState } from '../types';
import { UnreachableCaseError } from 'ts-essentials';
import { initialChartSettings } from '../../../utils/chartSettings/utils';
import { cloneDeep } from 'lodash';

export const toggleChartSettingsMeasure = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    measureIndex: number;
  }>,
) => {
  const { measureIndex, analysisId } = action.payload;
  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;
  const chartData = currAnalysis.chartData;

  const displayedMeasureSettings = chartSettings.displayedMeasureSettings;
  const displayedMeasureSetting = displayedMeasureSettings[measureIndex];

  // in AreaChart, StackedColumnChart, PieChart,
  // on enable measure will force to display only one measure.
  if (
    chartData?.queryType === QueryType.Expression &&
    [
      CHART_TYPES.PIE_CHART,
      CHART_TYPES.STACKED_COLUMN_CHART,
      CHART_TYPES.PERCENT_STACKED_AREA_CHART,
    ].includes(chartSettings.chartType)
  ) {
    displayedMeasureSettings.forEach((measure) => (measure.isVisible = false));
  }
  displayedMeasureSetting.isVisible = !displayedMeasureSetting.isVisible;
};

export const setChartSettingsMeasureVisibility = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    measuresVisibility: boolean[];
  }>,
) => {
  const { measuresVisibility, analysisId } = action.payload;
  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;
  const displayedMeasureSettings = chartSettings.displayedMeasureSettings;
  measuresVisibility.forEach((isVisible, index) => {
    displayedMeasureSettings[index].isVisible = isVisible;
  });
};

export const setChartSettingMeasure = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    newMeasureKey: string;
    measureIndex: number;
  }>,
) => {
  const { newMeasureKey, measureIndex, analysisId } = action.payload;
  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;
  const displayedMeasureSettings = chartSettings.displayedMeasureSettings;
  const displayedMeasureSetting = displayedMeasureSettings[measureIndex];
  displayedMeasureSetting.key = newMeasureKey;
  return;
};

export const toggleChartSettingsSlice = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    sliceKey: string;
  }>,
) => {
  const { analysisId, sliceKey } = action.payload;

  const analysis = draft[analysisId];
  assertNotNil(analysis);
  const chartSettings = analysis.settings.chartSettings;
  const displayedSliceSettings = chartSettings.displayedSliceSettings;
  const slice = displayedSliceSettings.find(
    (sliceSetting) => sliceSetting.key === sliceKey,
  );
  assertNotNil(slice);
  slice.isVisible = !slice.isVisible;
};

export const toggleTopChartSettingsSlices = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    sliceIndex: number;
  }>,
) => {
  const { sliceIndex, analysisId } = action.payload;
  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;

  chartSettings.displayedSliceSettings.forEach((displayedSlice, index) => {
    displayedSlice.isVisible = index < sliceIndex;
  });
};

export const selectChartSettingsSlice = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    sourceSliceKey: string;
    targetSliceKey: string;
  }>,
) => {
  const { analysisId, sourceSliceKey, targetSliceKey } = action.payload;

  const analysis = draft[analysisId];
  assertNotNil(analysis);
  const chartSettings = analysis.settings.chartSettings;
  const displayedSliceSettings = chartSettings.displayedSliceSettings;

  const sourceSliceIndex = displayedSliceSettings.findIndex(
    (slice) => slice.key === sourceSliceKey,
  );
  assertCondition(sourceSliceIndex !== -1);
  const targetSliceIndex = displayedSliceSettings.findIndex(
    (slice) => slice.key === targetSliceKey,
  );

  // By historical reasons, a list of slices to select can be longer
  // than displayedSliceSettings.
  // When we select the slice we have to either swap or replace source with target
  if (targetSliceIndex !== -1) {
    // swap slices
    [
      displayedSliceSettings[sourceSliceIndex].key,
      displayedSliceSettings[targetSliceIndex].key,
    ] = [
      displayedSliceSettings[targetSliceIndex].key,
      displayedSliceSettings[sourceSliceIndex].key,
    ];
  } else {
    // replace slices
    displayedSliceSettings[sourceSliceIndex].key = targetSliceKey;
  }
};

export const selectChartType = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    chartType: CHART_TYPES;
  }>,
) => {
  const { analysisId, chartType } = action.payload;
  const analysis = draft[analysisId];
  const chartSettings = analysis.settings.chartSettings;

  chartSettings.chartType = chartType;

  if (chartSettings.singleDayLabel) {
    chartSettings.singleDayLabel = '';
  }
  // special case for geo map, if switch to geo map, enable all measures
  if (chartType === CHART_TYPES.GEO_MAP) {
    chartSettings.displayedMeasureSettings.forEach(
      (measureSetting) => (measureSetting.isVisible = true),
    );
    if (
      analysis.metadata?.timeUnit !== TimeUnit.AllTime &&
      analysis.chartData &&
      'dates' in analysis.chartData
    ) {
      chartSettings.singleDayLabel = analysis?.chartData?.dates[0];
    }
  }

  // for chartTypes StackedAreaChart, StackedColumnChart, PieChart,
  // keep only one enabled measure from visible measures
  if (
    [
      CHART_TYPES.PIE_CHART,
      CHART_TYPES.STACKED_COLUMN_CHART,
      CHART_TYPES.PERCENT_STACKED_AREA_CHART,
    ].includes(chartType)
  ) {
    // KBT-946, default number type on Chart Type.
    if (
      chartType === CHART_TYPES.STACKED_COLUMN_CHART ||
      chartType === CHART_TYPES.PERCENT_STACKED_AREA_CHART
    ) {
      chartSettings.chartNumberType = ChartNumberType.Number;
    }

    const visibleMeasures = chartSettings.displayedMeasureSettings.filter(
      (measureSetting) => measureSetting.isVisible,
    );

    // keep only one enabled measure from visible measures
    if (visibleMeasures.length > 1) {
      let isEnabledFirstVisibleMeasure = false;
      visibleMeasures.forEach((measureSetting) => {
        if (!isEnabledFirstVisibleMeasure) {
          measureSetting.isVisible = true;
          isEnabledFirstVisibleMeasure = true;
        } else {
          measureSetting.isVisible = false;
        }
      });
    }
  }

  // if switch to pie chart or number chart, set singleDayLabel to the first date of the data
  if (
    chartType === CHART_TYPES.PIE_CHART ||
    chartType === CHART_TYPES.METRIC_CHART
  ) {
    assertCondition(analysis.chartData?.queryType === QueryType.Expression);
    const data = analysis.chartData?.data;
    assertCondition(data && data.length > 0);
    const lastDayData = data[data.length - 1];
    assertCondition(lastDayData?.date);
    chartSettings.singleDayLabel = String(lastDayData.date);
  }
};

export const setSingleDayLabel = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    singleDayLabel: string;
  }>,
) => {
  const { analysisId, singleDayLabel } = action.payload;
  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;
  chartSettings.singleDayLabel = singleDayLabel;
};

export const toggleChartSettingSort = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
  }>,
) => {
  const { analysisId } = action.payload;
  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;
  if (currAnalysis.metadata?.queryType === QueryType.Retention) {
    chartSettings.sort =
      chartSettings.sort === ChartSortEnum.ASC
        ? ChartSortEnum.DESC
        : ChartSortEnum.ASC;
  } else {
    const sortTypes = [
      ChartSortEnum.NONE,
      ChartSortEnum.ASC,
      ChartSortEnum.DESC,
    ];
    const currentSort = chartSettings.sort;
    const nextSortIndex =
      (sortTypes.indexOf(currentSort) + 1) % sortTypes.length;
    chartSettings.sort = sortTypes[nextSortIndex];
  }
  return;
};

export const toggleChartSettingsTopBreakdowns = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{ analysisId: string; topBreakdownsValue: number }>,
) => {
  const { analysisId, topBreakdownsValue } = action.payload;
  const chartSettings = draft[analysisId].settings.chartSettings;
  chartSettings.topBreakdownsValue = topBreakdownsValue;
  return;
};

export const setChartSettingChartNumberType = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    newType: ChartNumberType;
  }>,
) => {
  // Toggle between number and percent view
  const { analysisId, newType } = action.payload;
  draft[analysisId].settings.chartSettings.chartNumberType = newType;
  return;
};

export const setChartSettingChurn = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    isChurn: boolean;
  }>,
) => {
  const { analysisId, isChurn } = action.payload;
  draft[analysisId].settings.chartSettings.isChurn = isChurn;
  return;
};

export const setChartSettingsYAxisPosition = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    measureKey: string;
    yAxisPosition: YAxisPosition;
  }>,
) => {
  const { analysisId, measureKey, yAxisPosition } = action.payload;

  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;

  const measureSetting = findByKey(
    chartSettings.displayedMeasureSettings,
    measureKey,
  );
  measureSetting.yAxisPosition = yAxisPosition;
};

export const setChartSettingPathNodeCustomization = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    pathNodeCustomization: IPathNodeCustomization;
    pathBreadth: number;
  }>,
) => {
  const { analysisId, pathNodeCustomization, pathBreadth } = action.payload;

  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;

  const nodeName = pathNodeCustomization.name;

  let nodeSetting = chartSettings.pathNodeSettings.find(
    (p) => p.name === nodeName,
  );

  // create new node setting if needed
  if (!nodeSetting) {
    nodeSetting = { name: nodeName, othersConfig: [] };
    chartSettings.pathNodeSettings.push(nodeSetting);
  }

  const configAction = pathNodeCustomization.type;

  // for "in this step", simply toggle the setting
  // for "in whole path", replace all settings with merge / split
  switch (pathNodeCustomization.scope) {
    case NodeCustomizationScope.IN_THIS_STEP:
      const custSeq = pathNodeCustomization.seq;
      const seqConfig = nodeSetting.othersConfig.find((c) => c.seq === custSeq);
      if (!seqConfig) {
        nodeSetting.othersConfig.push({ seq: custSeq, action: configAction });
      } else {
        seqConfig.action = configAction;
      }
      break;
    case NodeCustomizationScope.IN_WHOLE_PATH:
      nodeSetting.othersConfig = [];
      // start from index 1 (second step) - merge / expand does not make
      // sense for the first step
      for (let seq = 1; seq <= pathBreadth; seq++) {
        nodeSetting.othersConfig.push({
          seq,
          action: configAction,
        });
      }
      break;
    default:
      throw new UnreachableCaseError(pathNodeCustomization);
  }
};

export const setChartSettingPathObservedNodeId = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    observedNodeId: MaybeNil<string>;
  }>,
) => {
  const { analysisId, observedNodeId } = action.payload;
  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;

  chartSettings.pathObservedNodeId = observedNodeId;
};

export const setChartSettingGeoMapPosition = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    geoMapConfig: Omit<IGeoMapConfig, 'isHeatmap' | 'displayedMeasureKey'>;
  }>,
) => {
  const { analysisId, geoMapConfig } = action.payload;
  const analysis = draft[analysisId];

  // this action would be called in a debounced function,
  // therefore, if original action targeted an unmount report, the analysis would be undefined.
  if (analysis?.settings) {
    const chartSettings = analysis.settings.chartSettings;

    chartSettings.geoMapConfig = {
      ...chartSettings.geoMapConfig,
      ...geoMapConfig,
    };
  }
};

export const setChartSettingGeoMapHeatmap = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    isHeatmap: boolean;
  }>,
) => {
  const { analysisId, isHeatmap } = action.payload;
  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;

  chartSettings.geoMapConfig = {
    ...chartSettings.geoMapConfig,
    isHeatmap,
  };
};

export const setChartSettingGeoMapDisplayedMeasureKey = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    displayedMeasureKey: string;
  }>,
) => {
  const { analysisId, displayedMeasureKey } = action.payload;
  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;

  chartSettings.geoMapConfig = {
    ...chartSettings.geoMapConfig,
    displayedMeasureKey,
  };
};

export const resetCentralChartSettings = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{ analysisId: string }>,
) => {
  const { analysisId } = action.payload;
  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;
  const defaultChartSettings = currAnalysis.defaultChartSettings;
  chartSettings.axisSettings = cloneDeep(initialChartSettings.axisSettings);
  chartSettings.displayedMeasureSettings.forEach((measureSetting) => {
    if (measureSetting.yAxisPosition) {
      delete measureSetting.yAxisPosition;
    }
  });

  // Reset FunnelAssignment from defaultChartSettings
  if (currAnalysis.metadata?.queryType === QueryType.Funnel) {
    chartSettings.isShowFunnelConversionLabels =
      defaultChartSettings.isShowFunnelConversionLabels;
    chartSettings.isShowFunnelBounceLabels =
      defaultChartSettings.isShowFunnelBounceLabels;
    chartSettings.isConversionView = defaultChartSettings.isConversionView;
    chartSettings.isFunnelClusterMode =
      defaultChartSettings.isFunnelClusterMode;
  }
};

export const setChartSettingYAxisConfig = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    yAxisPosition: YAxisPosition;
    config: Partial<ICentralYAxisSettings>;
  }>,
) => {
  const { analysisId, yAxisPosition, config } = action.payload;
  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;
  chartSettings.axisSettings.yAxisSettings[yAxisPosition] = {
    ...chartSettings.axisSettings.yAxisSettings[yAxisPosition],
    ...config,
  };
};

export const toggleIsApplyYAxisFormat = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{ analysisId: string; yAxisPosition: YAxisPosition }>,
) => {
  const { analysisId, yAxisPosition } = action.payload;
  const currAnalysis = draft[analysisId];
  const chartSettings = currAnalysis.settings.chartSettings;
  chartSettings.axisSettings.yAxisSettings[yAxisPosition].isApplyYAxisFormat =
    !chartSettings.axisSettings.yAxisSettings[yAxisPosition].isApplyYAxisFormat;
};
